import { createServerFn } from "@tanstack/react-start";

export interface InstagramPost {
  id: string;
  caption: string;
  permalink: string;
  mediaType: "IMAGE" | "VIDEO" | "CAROUSEL_ALBUM";
  imageUrl: string;
  timestamp: string;
}

export interface InstagramFeedResult {
  posts: InstagramPost[];
  username: string | null;
  error: string | null;
}

interface GraphMedia {
  id: string;
  caption?: string;
  media_type?: string;
  media_url?: string;
  thumbnail_url?: string;
  permalink?: string;
  timestamp?: string;
}

const LIMIT = 8;
const FIELDS = "id,caption,media_type,media_url,thumbnail_url,permalink,timestamp";
const GRAPH = "https://graph.facebook.com/v21.0";

type GraphResponse = {
  data?: GraphMedia[];
  error?: { message?: string; code?: number };
  instagram_business_account?: { id?: string };
};

async function graphGet(url: string): Promise<GraphResponse> {
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  const body = (await res.json().catch(() => ({}))) as GraphResponse;
  if (!res.ok || body.error) {
    console.error(`[instagram] Graph failed [${res.status}] ${url.split("?")[0]}: ${body.error?.message ?? ""}`);
    return { error: body.error ?? { message: `HTTP ${res.status}` } };
  }
  return body;
}

/** Resolve the IG Business account id: the configured id may already be it, or a Facebook Page id. */
async function resolveIgUserId(id: string, token: string): Promise<string | null> {
  const page = await graphGet(
    `${GRAPH}/${encodeURIComponent(id)}?fields=instagram_business_account{id}&access_token=${encodeURIComponent(token)}`,
  );
  if (page.instagram_business_account?.id) return page.instagram_business_account.id;

  // Business/system-user tokens: walk the pages the token manages.
  const accounts = (await graphGet(
    `${GRAPH}/me/accounts?fields=id,name,instagram_business_account{id}&limit=50&access_token=${encodeURIComponent(token)}`,
  )) as unknown as {
    data?: Array<{ id?: string; name?: string; instagram_business_account?: { id?: string } }>;
  };
  const match = (accounts.data ?? []).find((p) => p?.instagram_business_account?.id);
  if (match?.instagram_business_account?.id) {
    console.info(`[instagram] resolved IG account via page "${match.name ?? match.id}"`);
    return match.instagram_business_account.id;
  }
  return null;
}

function mapPosts(items: GraphMedia[]): InstagramPost[] {
  return items
    .slice(0, LIMIT)
    .map((m) => ({
      id: m.id,
      caption: (m.caption ?? "").slice(0, 220),
      permalink: m.permalink ?? "https://www.instagram.com/kentrosistemas/",
      mediaType: (m.media_type === "VIDEO"
        ? "VIDEO"
        : m.media_type === "CAROUSEL_ALBUM"
          ? "CAROUSEL_ALBUM"
          : "IMAGE") as InstagramPost["mediaType"],
      imageUrl: (m.media_type === "VIDEO" ? m.thumbnail_url : m.media_url) ?? m.media_url ?? "",
      timestamp: m.timestamp ?? "",
    }))
    .filter((p) => p.imageUrl.length > 0);
}

const TTL_MS = 5 * 60 * 1000;
let cache: { at: number; value: InstagramFeedResult } | null = null;
let resolvedIgId: string | null = null;

export const getInstagramFeed = createServerFn({ method: "GET" }).handler(
  async (): Promise<InstagramFeedResult> => {
    const token = process.env["INSTAGRAM_ACCESS_TOKEN"];
    const configuredId = process.env["INSTAGRAM_USER_ID"];

    if (!token || !configuredId) {
      return { posts: [], username: null, error: "not_configured" };
    }

    if (cache && Date.now() - cache.at < TTL_MS && cache.value.posts.length > 0) {
      return cache.value;
    }

    const qs = `?fields=${FIELDS}&limit=${LIMIT}&access_token=${encodeURIComponent(token)}`;

    try {
      // Reuse the previously resolved IG Business account when known.
      let feed = await graphGet(
        `${GRAPH}/${encodeURIComponent(resolvedIgId ?? configuredId)}/media${qs}`,
      );

      // Configured id is a Facebook Page (or a BM-managed page) linked to an IG account
      if (feed.error) {
        const igId = await resolveIgUserId(configuredId, token);
        if (igId) {
          resolvedIgId = igId;
          feed = await graphGet(`${GRAPH}/${encodeURIComponent(igId)}/media${qs}`);
        }
      }

      // Instagram-native (Basic Display / IG Login) token
      if (feed.error) {
        resolvedIgId = null;
        feed = await graphGet(`https://graph.instagram.com/me/media${qs}`);
      }

      if (feed.error || !feed.data) {
        return cache?.value ?? { posts: [], username: null, error: "api_error" };
      }

      const value: InstagramFeedResult = {
        posts: mapPosts(feed.data),
        username: null,
        error: null,
      };
      cache = { at: Date.now(), value };
      return value;
    } catch (err) {
      console.error("[instagram] fetch failed", err);
      return cache?.value ?? { posts: [], username: null, error: "network_error" };
    }
  },
);

